// firebase-config.js
import { initializeApp } from 'firebase/app';
import { getAuth, indexedDBLocalPersistence, initializeAuth } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { getMessaging } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';
import env from './utils/loadEnv';
import { getStorage } from 'firebase/storage';
import * as Sentry from '@sentry/react';
import { captureException, setSentryTag } from './utils/sentry';

// Log platform info for debugging
const platform = Capacitor.getPlatform();
const isNativePlatform = Capacitor.isNativePlatform();
const isIOS = platform === 'ios';

// Add platform info to Sentry for better debugging
setSentryTag('platform', platform);
setSentryTag('isNative', String(isNativePlatform));
setSentryTag('firebaseEnv', process.env.NODE_ENV || 'unknown');

// Add breadcrumb for Firebase initialization start
Sentry.addBreadcrumb({
  category: 'firebase',
  message: `Firebase initializing on platform: ${platform}, isNative: ${isNativePlatform}`,
  level: 'info',
});

// Firebase configuration with fallback values for safety
const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY || "AIzaSyDTlnZpDHMQXRRvCrk-TMPJwT5gVzYcUiI",
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || "mgp-dev-a0a0a.firebaseapp.com",
  databaseURL: env.VITE_FIREBASE_DATABASE_URL || "https://mgp-dev-a0a0a.firebaseio.com",
  projectId: env.VITE_FIREBASE_PROJECT_ID || "mgp-dev-a0a0a",
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET || "mgp-dev-a0a0a.appspot.com",
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID || "1234567890",
  appId: env.VITE_FIREBASE_APP_ID || "1:1234567890:web:a0a0a0a0a0a0a0a0a0a0a0",
};

// Log Firebase config (without sensitive values) for debugging
console.log(`Firebase config using projectId: ${firebaseConfig.projectId}`);


// Initialize Firebase app with error handling
let app: ReturnType<typeof initializeApp> | undefined;
try {
  app = initializeApp(firebaseConfig);
  console.log('Firebase app initialized successfully');

  // Log successful initialization to Sentry
  Sentry.addBreadcrumb({
    category: 'firebase',
    message: 'Firebase app initialized successfully',
    level: 'info',
  });
} catch (error) {
  console.error('Error initializing Firebase app:', error);

  // Log error to Sentry
  captureException(error instanceof Error ? error : new Error('Firebase app initialization failed'), {
    firebaseConfig: { projectId: firebaseConfig.projectId }, // Only log non-sensitive info
    platform,
    isNative: isNativePlatform,
  });

  // Attempt recovery for iOS TestFlight builds
  if (isIOS && isNativePlatform) {
    console.log('Attempting recovery for iOS Firebase initialization...');
    Sentry.addBreadcrumb({
      category: 'firebase',
      message: 'Attempting recovery for iOS Firebase initialization',
      level: 'warning',
    });

    try {
      // Try again with a delay
      setTimeout(() => {
        if (!app) {
          try {
            app = initializeApp(firebaseConfig);
            console.log('Firebase app recovery successful');

            // Log recovery success to Sentry
            Sentry.addBreadcrumb({
              category: 'firebase',
              message: 'Firebase app recovery successful',
              level: 'info',
            });
          } catch (innerError) {
            console.error('Inner recovery attempt failed:', innerError);
            captureException(innerError instanceof Error ? innerError : new Error('Firebase inner recovery failed'));
          }
        }
      }, 1000);
    } catch (recoveryError) {
      console.error('Firebase app recovery failed:', recoveryError);
      captureException(recoveryError instanceof Error ? recoveryError : new Error('Firebase recovery attempt failed'));
    }
  }
}

// Initialize and export Firebase services
let db: ReturnType<typeof getDatabase> | undefined;
let storage: ReturnType<typeof getStorage> | undefined;
try {
  if (app) {
    db = getDatabase(app);
    storage = getStorage(app);
    console.log('Firebase services initialized successfully');

    // Log successful services initialization to Sentry
    Sentry.addBreadcrumb({
      category: 'firebase',
      message: 'Firebase services (DB, Storage) initialized successfully',
      level: 'info',
    });
  } else {
    console.error('Cannot initialize Firebase services: app is undefined');

    // Log error to Sentry
    Sentry.captureMessage('Cannot initialize Firebase services: app is undefined', {
      level: 'error',
      tags: { component: 'firebase-services' },
    });
  }
} catch (error) {
  console.error('Error initializing Firebase services:', error);
  captureException(error instanceof Error ? error : new Error('Firebase services initialization failed'));
}

export { db, storage };

// Export authentication service (handles native and web platforms)
let auth: ReturnType<typeof getAuth> | ReturnType<typeof initializeAuth> | undefined;

try {
  if (app) {
    if (isNativePlatform) {
      auth = initializeAuth(app, {
        persistence: indexedDBLocalPersistence,
      });
      console.log('Auth initialized for native platform');

      // Log successful auth initialization to Sentry
      Sentry.addBreadcrumb({
        category: 'firebase',
        message: 'Firebase Auth initialized for native platform',
        level: 'info',
      });
    } else {
      auth = getAuth(app);
      console.log('Auth initialized for web platform');

      // Log successful auth initialization to Sentry
      Sentry.addBreadcrumb({
        category: 'firebase',
        message: 'Firebase Auth initialized for web platform',
        level: 'info',
      });
    }
  } else {
    console.error('Cannot initialize auth: app is undefined');

    // Log error to Sentry
    Sentry.captureMessage('Cannot initialize Firebase Auth: app is undefined', {
      level: 'error',
      tags: { component: 'firebase-auth' },
    });
  }
} catch (error) {
  console.error('Auth initialization error:', error);
  captureException(error instanceof Error ? error : new Error('Firebase Auth initialization failed'));

  // Fallback to default auth if native initialization fails
  try {
    if (app) {
      auth = getAuth(app);
      console.log('Fallback auth initialization successful');

      // Log fallback success to Sentry
      Sentry.addBreadcrumb({
        category: 'firebase',
        message: 'Firebase Auth fallback initialization successful',
        level: 'warning',
      });
    }
  } catch (fallbackError) {
    console.error('Fallback auth initialization failed:', fallbackError);
    captureException(fallbackError instanceof Error ? fallbackError : new Error('Firebase Auth fallback initialization failed'));

    // Last resort recovery for iOS TestFlight builds
    if (isIOS && app) {
      Sentry.addBreadcrumb({
        category: 'firebase',
        message: 'Attempting delayed Firebase Auth initialization for iOS',
        level: 'warning',
      });

      setTimeout(() => {
        try {
          if (!auth && app) {
            auth = getAuth(app);
            console.log('Delayed auth initialization successful');

            // Log delayed success to Sentry
            Sentry.addBreadcrumb({
              category: 'firebase',
              message: 'Delayed Firebase Auth initialization successful',
              level: 'info',
            });
          }
        } catch (delayedError) {
          console.error('Delayed auth initialization failed:', delayedError);
          captureException(delayedError instanceof Error ? delayedError : new Error('Delayed Firebase Auth initialization failed'));
        }
      }, 1500);
    }
  }
}

export { auth };

// Export messaging service (only for web with service worker support)
export const messaging =
  typeof window !== 'undefined' && 'serviceWorker' in navigator
    ? getMessaging(app)
    : null;